import { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import StyledButton from '../components/StyledButton';
import InputContext from '../components/InputContext';
import "./AddRecipe.css";

const baseURL = process.env.REACT_APP_API_URL;


function AddRecipePage() {
    const [recipeName, setRecipeName] = useState('');
    const [ingredients, setIngredients] = useState('');
    const [steps, setSteps] = useState([]);
    const [stepInput, setStepInput] = useState('');
    const [imageFile, setImageFile] = useState(null);
    const { username } = useContext(InputContext);
    const [totalCalories, setTotalCalories] = useState(null);
    const navigate = useNavigate();

    const [showStepModal, setShowStepModal] = useState(false);
    const [editingStepIndex, setEditingStepIndex] = useState(null);
    const [newStepText, setNewStepText] = useState('');

    const APP_ID = process.env.REACT_APP_NUTRITIONIX_APP_ID;
    const APP_KEY = process.env.REACT_APP_NUTRITIONIX_APP_KEY;

    const calculateCalories = async () => {
        try {
            const response = await fetch("https://trackapi.nutritionix.com/v2/natural/nutrients", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "x-app-id": APP_ID,
                    "x-app-key": APP_KEY
                },
                body: JSON.stringify({ query: ingredients })
            });

            const data = await response.json();
            if (data.foods) {
                const total = data.foods.reduce((sum, food) => sum + food.nf_calories, 0);
                setTotalCalories(total.toFixed(2));
            } else {
                setTotalCalories("Error calculating calories.");
            }
        } catch (err) {
            console.error(err);
            setTotalCalories("Error calculating calories.");
        }
    };

    const handleImageChange = (e) => setImageFile(e.target.files[0]);

    const handleSave = async () => {
        const formData = new FormData();
        formData.append('title', recipeName);
        formData.append('ingredients', ingredients);
        formData.append('steps', steps.join('\n'));
        formData.append('author', username);
        if (totalCalories !== null) {
            formData.append('calories', totalCalories);
        }
        if (imageFile) formData.append('image', imageFile);

        try {
        const res = await fetch(`${baseURL}/recipes`, {
            method: 'POST',
            body: formData
        });

        if (res.ok) navigate('/home');
        else console.error("Failed to add recipe");
        } catch (err) {
        console.error("Error:", err);
        }
    };

    const handleCancel = () => navigate('/home');

    const handleAddStepInline = () => {
        if (stepInput.trim()) {
        setSteps([...steps, stepInput.trim()]);
        setStepInput('');
        }
    };

    const handleEditStep = (index) => {
        setEditingStepIndex(index);
        setNewStepText(steps[index]);
        setShowStepModal(true);
    };

    const handleDeleteStep = (index) => {
        setSteps(steps.filter((_, i) => i !== index));
    };

    const handleMoveStep = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= steps.length) return;
        const updated = [...steps];
        [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
        setSteps(updated);
    };

    const handleCancelAddStep = () => {
        setShowStepModal(false);
        setEditingStepIndex(null);
        setNewStepText('');
    };

    return (
        <>
            <Header />
            <div style={{ padding: '40px 20px', maxWidth: '600px', margin: '0 auto' }}>
                <h2>Recipe Name</h2>
                <input
                    className="form-input"
                    type="text"
                    value={recipeName}
                    onChange={(e) => setRecipeName(e.target.value)}
                    placeholder="Enter recipe name"
                    style={inputStyle}
                />

                <h2>Ingredients</h2>
                <textarea
                    className="ingredients-form-textarea"
                    value={ingredients}
                    onChange={(e) => setIngredients(e.target.value)}
                    placeholder="List ingredients... (One ingredient per line. Specify quantity to calculate calories.)"
                    rows={4}
                    style={inputStyle}
                />
                <StyledButton type="button" onClick={calculateCalories}>Calculate Calories</StyledButton>

                {totalCalories && (
                    <p style={{ marginTop: '10px' }}>
                        <strong>Total Calories:</strong> {totalCalories} calories
                    </p>
                )}

                <h2 style={{ marginTop: '40px' }}>Steps</h2>
                {steps.map((step, index) => (
                    <div key={index} className="step-tile">
                        <div style={{ flex: 1 }}>{index + 1}. {step}</div>
                        <div className="step-buttons">
                            <button onClick={() => handleEditStep(index)}>✎</button>
                            <button onClick={() => handleDeleteStep(index)}>×</button>
                            <button onClick={() => handleMoveStep(index, -1)}>↑</button>
                            <button onClick={() => handleMoveStep(index, 1)}>↓</button>
                        </div>
                    </div>
                ))}

                <div className="step-input-row">
                    <textarea
                        value={stepInput}
                        onChange={(e) => setStepInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleAddStepInline();
                            }
                        }}
                        placeholder="Type a step and press Enter or click + button"
                        rows={2}
                    />
                    <button onClick={handleAddStepInline}>+</button>
                </div>

                <h2 style={{ marginTop: '24px' }}>Image</h2>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ marginBottom: '24px' }}
                />

                <div style={{ marginTop: '20px' }}>
                    <StyledButton type="button" onClick={handleSave}>Save</StyledButton>
                    <StyledButton type="button" onClick={handleCancel}>Cancel</StyledButton>
                </div>
            </div>
            {showStepModal && (
                <div className="modal-overlay" onClick={handleCancelAddStep}>
                    <div className="modal-box" onClick={(e) => e.stopPropagation()}>
                        <h3>{editingStepIndex !== null ? 'Edit Step' : 'Add a New Step'}</h3>
                        <textarea
                            rows={4}
                            value={newStepText}
                            onChange={(e) => setNewStepText(e.target.value)}
                            placeholder="Describe the step..."
                            style={{
                                width: '100%',
                                padding: '10px',
                                fontSize: '16px',
                                borderRadius: '6px',
                                border: '1px solid #5E503F',
                                marginBottom: '12px',
                                resize: 'none'
                             }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                            <StyledButton type="button" onClick={() => {
                                if (newStepText.trim()) {
                                    if (editingStepIndex !== null) {
                                        const updatedSteps = [...steps];
                                        updatedSteps[editingStepIndex] = newStepText.trim();
                                        setSteps(updatedSteps);
                                    } else {
                                        setSteps([...steps, newStepText.trim()]);
                                    }
                                }
                                setNewStepText('');
                                setEditingStepIndex(null);
                                setShowStepModal(false);
                            }}>
                                {editingStepIndex !== null ? 'Save' : 'Add'}
                            </StyledButton>
                            <StyledButton type="button" onClick={handleCancelAddStep}>Cancel</StyledButton>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
}

const inputStyle = {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '6px',
    border: '1px solid #5E503F',
    marginBottom: '24px',
    resize: 'none'
};

export default AddRecipePage;
